import { ErrorType } from './consts'
import {
  ApiUrls,
  ApiUrlsV2,
  AUTH_API_PREFIX,
  AUTH_STATE_CHANGED_TYPE,
  DEFAULT_NODE_ACCESS_SCOPE,
  EVENTS,
  LOGIN_STATE_CHANGED_TYPE,
} from '../auth/consts'

import { AuthClient, SimpleStorage } from './interface'

import {
  Credentials,
  ResponseError,
  RequestOptions,
  RequestFunction,
  OAuth2ClientOptions,
  AuthClientRequestOptions,
} from './models'

import { uuidv4 } from '../utils/uuid'
import { getPathName } from '../utils/index'

import { SinglePromise } from '../utils/function/single-promise'
import { weBtoa } from '../utils/base64'
import { isMp } from '../utils/mp'
import { AuthOptions } from '../auth/apis'
import { ICloudbaseConfig } from '@cloudbase/types'
import { langEvent } from '@cloudbase/utilities'

const RequestIdHeaderName = 'x-request-id'
const DeviceIdHeaderName = 'x-device-id'
const DeviceIdSectionName = 'device_id'

declare const wx: any

export interface ToResponseErrorOptions {
  error?: ErrorType
  error_description?: string | null
  error_uri?: string | null
  details?: any | null
}

export const defaultRequest: RequestFunction = async function <T>(url: string, options?: RequestOptions): Promise<T> {
  let result: T | null = null
  let responseError: ResponseError | null = null
  try {
    // Objects must be copied to prevent modification of data such as body.
    const copyOptions = Object.assign({}, options)
    if (!copyOptions.method) {
      copyOptions.method = 'GET'
    }
    if (copyOptions.body && typeof copyOptions.body !== 'string') {
      copyOptions.body = JSON.stringify(copyOptions.body)
    }
    const responseResult: Response = await fetch(url, copyOptions)
    const jsonResponse = await responseResult.json()
    if (jsonResponse?.error) {
      responseError = jsonResponse as ResponseError
      responseError.error_uri = new URL(url).pathname
    } else {
      result = jsonResponse as T
    }
  } catch (error) {
    responseError = {
      error: ErrorType.UNREACHABLE,
      error_description: error.message,
      error_uri: new URL(url).pathname,
    }
  }
  if (responseError) {
    throw responseError
  } else {
    return result
  }
}

export const toResponseError = (error: ResponseError | Error, options?: ToResponseErrorOptions): ResponseError => {
  let responseError: ResponseError
  const formatOptions: ToResponseErrorOptions = options || {}
  if (error instanceof Error) {
    responseError = {
      error: formatOptions.error || ErrorType.LOCAL,
      error_description: formatOptions.error_description || error.message,
      error_uri: formatOptions.error_uri,
      details: formatOptions.details || error.stack,
    }
  } else {
    const formatError: ToResponseErrorOptions = error || {}
    responseError = {
      error: formatOptions.error || formatError.error || ErrorType.LOCAL,
      error_description: formatOptions.error_description || formatError.error_description,
      error_uri: formatOptions.error_uri || formatError.error_uri,
      details: formatOptions.details || formatError.details,
    }
  }
  return responseError
}

/**
 * Generate request id.
 * @return {string}
 */
export function generateRequestId(): string {
  return uuidv4()
}

/**
 * Default Storage.
 */
class DefaultStorage implements SimpleStorage {
  /**
   * 缓存key统一使用后缀区分
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  private readonly _env: string

  constructor(opts?: { env: string }) {
    this._env = opts?.env || ''
  }
  /**
   * Get item.
   * @param {string} key
   */
  async getItem(key: string): Promise<string | null> {
    return window.localStorage.getItem(`${key}${this._env}`)
  }

  /**
   * Remove item.
   * @param {string} key
   */
  async removeItem(key: string): Promise<void> {
    window.localStorage.removeItem(`${key}${this._env}`)
  }

  /**
   * Set item.
   * @param {string} key
   * @param {string} value
   */
  async setItem(key: string, value: string): Promise<void> {
    window.localStorage.setItem(`${key}${this._env}`, value)
  }

  /**
   * Get item sync.
   * @param {string} key
   */
  getItemSync(key: string): string | null {
    return window.localStorage.getItem(`${key}${this._env}`)
  }

  /**
   * Remove item sync.
   * @param {string} key
   */
  removeItemSync(key: string): void {
    window.localStorage.removeItem(`${key}${this._env}`)
  }

  /**
   * Set item sync.
   * @param {string} key
   * @param {string} value
   */
  setItemSync(key: string, value: string): void {
    window.localStorage.setItem(`${key}${this._env}`, value)
  }
}

export const defaultStorage = new DefaultStorage()

type CredentialsChangeContext = {
  isInit: boolean
  hadCredentials: boolean
}

interface LocalCredentialsOptions {
  tokenSectionName: string
  storage: SimpleStorage
  clientId: string
  credentials?: Credentials
  /** 每次 setCredentials 写入后触发，用于统一事件广播和跨标签页同步 */
  onCredentialsChange?: (credentials: Credentials | null, context: CredentialsChangeContext) => void
}

/**
 * Check if credentials is expired.
 * @param {Credentials} credentials
 * @return {boolean}
 */
function isCredentialsExpired(credentials: Credentials): boolean {
  let isExpired = true
  if (credentials?.expires_at && credentials?.access_token) {
    isExpired = credentials.expires_at < new Date()
  }
  return isExpired
}

/**
 * Local credentials.
 * storage 为凭证的唯一可信来源（source of truth）。
 * getCredentials 每次都从 storage 读取最新凭证，内存缓存（this.credentials）
 * 仅作为 accessKey 身份等无 storage token 场景下的降级值，
 * 从而保证多标签页/多实例间登录、登出、刷新状态实时一致。
 */
class LocalCredentials {
  private tokenSectionName: string

  private storage: SimpleStorage

  private clientId: string

  private credentials: Credentials | null = null
  // 来自accessKey的身份，不持久化
  private accessKeyCredentials: Credentials | null = null

  private singlePromise: SinglePromise = null

  /**
   * setCredentials 写入后的回调，用于统一事件广播和跨标签页同步
   */
  private onCredentialsChange?: (credentials: Credentials | null, context: CredentialsChangeContext) => void

  /**
   * constructor
   * @param {LocalCredentialsOptions} options
   */
  constructor(options: LocalCredentialsOptions) {
    this.tokenSectionName = options.tokenSectionName
    this.storage = options.storage
    this.clientId = options.clientId
    this.singlePromise = new SinglePromise({ clientId: this.clientId })
    this.credentials = options.credentials || null
    this.onCredentialsChange = options.onCredentialsChange
  }

  /**
   * 用外部凭证同步内存缓存（不写 storage）。
   * 供 BroadcastChannel 接收端使用：storage 已由发送端写入，
   * 这里仅同步内存缓存即可，避免重复持久化。
   * @param {Credentials | null} credentials
   */
  public syncMemoryCredentials(credentials: Credentials | null): void {
    this.credentials = credentials
  }

  /**
   * 获取当前内存中的凭证快照（不读 storage）。
   * 供 visibilitychange 等场景对比 storage 与内存是否一致。
   */
  public getMemoryCredentials(): Credentials | null {
    return this.credentials
  }

  public getStorageCredentialsSync(): Credentials | null {
    let credentials: Credentials = null
    const tokenStr: string = this.storage.getItemSync(this.tokenSectionName)
    if (tokenStr !== undefined && tokenStr !== null) {
      try {
        credentials = JSON.parse(tokenStr)
        if (credentials?.expires_at) {
          credentials.expires_at = new Date(credentials.expires_at)
        }
      } catch (error) {
        this.storage.removeItem(this.tokenSectionName)
        credentials = null
      }
    }
    return credentials
  }

  /**
   * setCredentials Provides an alternative fetch api request implementation with auth credentials
   * @param {Credentials} credentials
   * @param {object} [options]
   * @param {boolean} [options.isInit] 是否为初始化阶段写入（此时事件类型为 INITIAL_SESSION）
   */
  public async setCredentials(
    credentials?: Credentials,
    options?: { isInit?: boolean },
  ): Promise<void> {
    // 写入前记录是否已有有效凭证，用于回调中判断事件类型
    const hadCredentials = !!(this.credentials && this.credentials.access_token)

    if (credentials?.expires_in) {
      if (!credentials?.expires_at) {
        credentials.expires_at = new Date(Date.now() + (credentials.expires_in - 30) * 1000)
      }
      if (this.storage) {
        const tokenStr: string = JSON.stringify(credentials)
        await this.storage.setItem(this.tokenSectionName, tokenStr)
      }
      this.credentials = credentials
    } else {
      if (this.storage) {
        await this.storage.removeItem(this.tokenSectionName)
      }
      this.credentials = null
    }

    // 写入完成后统一触发回调（事件广播 + 跨标签页同步）
    if (this.onCredentialsChange) {
      this.onCredentialsChange((credentials || null) as Credentials | null, {
        isInit: options?.isInit ?? false,
        hadCredentials,
      })
    }
  }

  /**
   * set credentials from accessKey
   * @param {Credentials} credentials
   */
  public setAccessKeyCredentials(credentials?: Credentials) {
    this.accessKeyCredentials = credentials
    // node sdk 场景下直接使用accessKey身份
    if (credentials.scope === DEFAULT_NODE_ACCESS_SCOPE) {
      this.credentials = credentials
    }
  }

  /**
   * Get credentials.
   * storage 作为 source of truth：每次都从 storage 读取最新凭证，
   * 以便实时感知其他标签页/实例的登录、登出、刷新，避免使用过期或已登出的内存缓存。
   * 内存缓存仅在 accessKey 身份（storage 无 token）等场景下作为降级使用。
   * @return {Promise<Credentials | null>}
   */
  public async getCredentials(): Promise<Credentials | null> {
    return this.singlePromise.run('getCredentials', async () => {
      const { credentials, isAccessKeyCredentials } = await this.getStorageCredentials()
      if (isAccessKeyCredentials) {
        return credentials
      }
      this.credentials = credentials
      return this.credentials
    })
  }

  /**
   * Get storage credentials.
   */
  private async getStorageCredentials(): Promise<{ credentials: Credentials | null; isAccessKeyCredentials: boolean }> {
    return this.singlePromise.run('_getStorageCredentials', async () => {
      let credentials: Credentials = null
      let isAccessKeyCredentials = false
      const tokenStr: string = await this.storage.getItem(this.tokenSectionName)
      if (!!tokenStr) {
        try {
          credentials = JSON.parse(tokenStr)
          if (credentials?.expires_at) {
            credentials.expires_at = new Date(credentials.expires_at)
          }
        } catch (error) {
          await this.storage.removeItem(this.tokenSectionName)
          credentials = null
        }
      } else {
        credentials = this.accessKeyCredentials || null
        isAccessKeyCredentials = true
      }
      return { credentials, isAccessKeyCredentials }
    })
  }
}

/**
 * OAuth2Client
 */
export class OAuth2Client implements AuthClient {
  private static defaultRetry = 2
  private static minRetry = 0
  private static maxRetry = 5
  private static retryInterval = 1000

  public localCredentials: LocalCredentials
  /**
   * Keeps track of the async client initialization.
   * When null or not yet resolved the auth state is `unknown`
   * Once resolved the auth state is known and it's safe to call any further client methods.
   */
  public initializePromise: Promise<{ error: Error | null }> | null = null
  protected lockAcquired = false
  protected pendingInLock: Promise<any>[] = []
  protected logDebugMessages: boolean
  protected getInitialSession?: () => Promise<{
    data: { session: Credentials; user?: any } | null
    error: Error | null
  }>
  private apiOrigin: string
  private getApiOriginFunc?: () => string
  private apiPath: string
  private clientId: string
  private i18n: ICloudbaseConfig['i18n']
  private retry: number
  private clientSecret?: string
  private baseRequest: <T>(url: string, options?: RequestOptions) => Promise<T>
  private storage: SimpleStorage
  private deviceID?: string
  private tokenInURL?: boolean
  private refreshTokenFunc: (refreshToken?: string, credentials?: Credentials) => Promise<Credentials>
  private headers?: { [key: string]: string }
  private singlePromise: SinglePromise = null
  private anonymousSignInFunc: (Credentials) => Promise<Credentials | void>
  private wxCloud: any
  private useWxCloud: boolean
  private eventBus: any
  private basicAuth: string
  private onCredentialsError: AuthOptions['onCredentialsError'] | undefined
  private onInitialSessionObtained?: (data: { session: Credentials; user?: any }, error?: any) => void | Promise<void>
  // 跨标签页广播通道（仅 web 环境，小程序/node 无 BroadcastChannel）
  private broadcastChannel?: any
  // BroadcastChannel 的频道名，与 storage key 一致
  private broadcastChannelName?: string
  // 页面可见性变化监听器，destroy 时用于卸载
  private visibilityEventHandler?: (() => Promise<any>) | null
  // 标记 initialize() 是否已通过 setCredentials({isInit:true}) 触发过 INITIAL_SESSION
  private _initSessionEmitted = false

  /**
   * constructor
   * @param {OAuth2ClientOptions} options
   */
  constructor(options: OAuth2ClientOptions) {
    if (!options.clientSecret) {
      options.clientSecret = ''
    }

    if (!options.clientId && options.env) {
      options.clientId = options.env
    }

    this.apiOrigin = options.apiOrigin
    this.getApiOriginFunc = options.getApiOrigin
    this.apiPath = options.apiPath || AUTH_API_PREFIX
    this.clientId = options.clientId
    this.i18n = options.i18n
    this.eventBus = options.eventBus
    this.singlePromise = new SinglePromise({ clientId: this.clientId })
    this.retry = this.formatRetry(options.retry, OAuth2Client.defaultRetry)
    if (options.baseRequest) {
      this.baseRequest = options.baseRequest
    } else {
      this.baseRequest = defaultRequest
    }
    this.tokenInURL = options.tokenInURL
    this.headers = options.headers
    // @ts-ignore
    this.storage = options.storage || defaultStorage
    this.localCredentials = new LocalCredentials({
      tokenSectionName: `credentials_${options.clientId}`,
      storage: this.storage,
      clientId: options.clientId,
      // 统一的事件广播回调，所有写操作（登录/刷新/登出）都会触发
      onCredentialsChange: this._handleCredentialsChange.bind(this),
    })

    this.clientSecret = options.clientSecret
    if (options.clientId) {
      this.basicAuth = `Basic ${weBtoa(`${options.clientId}:${this.clientSecret}`)}`
    }
    this.wxCloud = options.wxCloud
    try {
      if (isMp()) {
        this.useWxCloud = options.useWxCloud
        if (this.wxCloud === undefined && options.env) {
          wx.cloud.init({ env: options.env })
          this.wxCloud = wx.cloud
        }
      }
    } catch (error) {
      //
    }
    this.refreshTokenFunc = options.refreshTokenFunc || this.defaultRefreshTokenFunc
    this.anonymousSignInFunc = options.anonymousSignInFunc
    this.onCredentialsError = options.onCredentialsError

    // New options for session detection
    this.getInitialSession = options.getInitialSession
    this.onInitialSessionObtained = options.onInitialSessionObtained
    this.logDebugMessages = options.debug ?? false

    langEvent.bus.on(langEvent.LANG_CHANGE_EVENT, (params: any) => {
      this.i18n = params.data?.i18n || this.i18n
    })

    // 初始化跨标签页广播通道（仅 web 环境生效）
    this._initBroadcastChannel()

    // 注册页面可见性监听（标签页隐藏→恢复时从 storage 补同步）
    this._initVisibilityListener()

    // Note: Auto-initialize is NOT called here to allow CloudbaseOAuth to set getInitialSession first
    // CloudbaseOAuth.constructor will call initialize() after setting up the callback
  }

  /**
   * Sets the getInitialSession callback.
   * This callback is called during initialize() to get the initial session (e.g., from OAuth callback in URL).
   * @param callback The callback function to get initial session
   */
  public setGetInitialSession(callback: () => Promise<{
    data: { session: Credentials; user?: any } | null
    error: Error | null
  }>,): void {
    this.getInitialSession = callback
  }

  /**
   * Sets the onInitialSessionObtained callback.
   * This callback is invoked after initial session is obtained and stored,
   * allowing upper layers to handle user info storage.
   * @param callback The callback function to handle session and user data
   */
  public setOnInitialSessionObtained(callback: (data: { session: Credentials; user?: any }) => void | Promise<void>,): void {
    this.onInitialSessionObtained = callback
  }

  /**
   * Initializes the client session either from the url or from storage.
   * This method is automatically called when instantiating the client with detectSessionInUrl=true,
   * but should also be called manually when checking for an error from an auth redirect.
   * @param onInitialSessionObtained Optional callback to set before initialization starts
   */
  async initialize(func?: Promise<{ error: Error | null }>): Promise<{ error: Error | null }> {
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    if (this.initializePromise) {
      return await this.initializePromise
    }

    if (func !== undefined) {
      this.initializePromise = func
      return await this.initializePromise
    }

    this.initializePromise = (async () => await this._acquireLock(-1, async () => await this._initialize()))()

    return await this.initializePromise
  }

  /**
   * setCredentials Provides an alternative fetch api request implementation with auth credentials
   * @param {Credentials} credentials
   * @return {Promise<void>}
   */
  public async setCredentials(credentials?: Credentials): Promise<void> {
    // If initialization is in progress, wait for it first
    await this.initializePromise
    return this._acquireLock(-1, async () => this.localCredentials.setCredentials(credentials))
  }

  /**
   * 标记 initialize() 期间是否已触发 INITIAL_SESSION。
   * Auth 层使用此标记判断是否需要自行触发，避免重复。
   */
  public get initSessionEmitted(): boolean {
    return this._initSessionEmitted
  }

  /**
   * 卸载 BroadcastChannel，释放资源。
   * 页面卸载 / 销毁 client 时调用，避免内存泄漏。
   */
  public destroy(): void {
    try {
      if (this.broadcastChannel && typeof this.broadcastChannel.close === 'function') {
        this.broadcastChannel.close()
      }
    } catch (error) {
      this._debug('#destroy', 'close BroadcastChannel error', error)
    } finally {
      this.broadcastChannel = null
    }

    try {
      const callback = this.visibilityEventHandler
      this.visibilityEventHandler = undefined
      if (callback && typeof window !== 'undefined' && typeof document !== 'undefined' && typeof window.removeEventListener === 'function') {
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        window.removeEventListener('visibilitychange', callback)
      }
    } catch (error) {
      this._debug('#destroy', 'remove visibility listener error', error)
    }
  }

  /**
   * set credentials from accessKey
   * @param {Credentials} credentials
   */
  public setAccessKeyCredentials(credentials?: Credentials) {
    return this.localCredentials.setAccessKeyCredentials(credentials)
  }

  /**
   * getAccessToken return a validate access token
   */
  public async getAccessToken(): Promise<string> {
    // If initialization is in progress, wait for it first
    await this.initializePromise
    const credentials: Credentials = await this.getCredentials()
    // node sdk 场景下直接返回access_token
    if (credentials?.scope === DEFAULT_NODE_ACCESS_SCOPE) {
      return Promise.resolve(credentials.access_token)
    }
    if (credentials?.access_token) {
      return Promise.resolve(credentials.access_token)
    }
    const respErr: ResponseError = { error: ErrorType.UNAUTHENTICATED }
    return Promise.reject(respErr)
  }

  /**
   * request http like simple fetch api, exp:request('/v1/user/me', {withCredentials:true})
   * @param {string} url
   * @param {AuthClientRequestOptions} options
   */
  public async request<T>(url: string, options?: AuthClientRequestOptions): Promise<T> {
    if (!options) {
      options = {}
    }
    const retry: number = this.formatRetry(options.retry, this.retry)
    options.headers = { ...options.headers, [this.i18n?.LANG_HEADER_KEY]: this.i18n?.lang }
    if (this.headers) {
      options.headers = {
        ...this.headers,
        ...options.headers,
      }
    }
    if (!options.headers[RequestIdHeaderName]) {
      options.headers[RequestIdHeaderName] = generateRequestId()
    }
    if (!options.headers[DeviceIdHeaderName]) {
      const deviceId = await this.getDeviceId()
      options.headers[DeviceIdHeaderName] = deviceId
    }
    if (options?.withBasicAuth && this.basicAuth) {
      options.headers.Authorization = this.basicAuth
    }
    if (options?.withCredentials) {
      // Use custom getCredentials function if provided, otherwise use default
      // Custom getCredentials avoids default getCredentials() call which may cause deadlock during initialization
      const credentials = options.getCredentials ? await options.getCredentials() : await this.getCredentials()
      if (credentials) {
        if (this.tokenInURL) {
          if (url.indexOf('?') < 0) {
            url += '?'
          }
          url += `access_token=${credentials.access_token}`
        } else {
          options.headers.Authorization = `${credentials.token_type} ${credentials.access_token}`
        }
      }
    } else {
      if (this.clientId && url.indexOf('client_id') < 0) {
        url += url.indexOf('?') < 0 ? '?' : '&'
        url += `client_id=${this.clientId}`
      }
    }
    if (url.startsWith('/')) {
      // 优先使用动态获取函数，以支持 registerEndPointWithKey 后端点变更生效
      const origin = this.getApiOriginFunc ? this.getApiOriginFunc() : this.apiOrigin
      url = `${origin}${this.apiPath}${url}`
    }
    let response: T | null = null
    const maxRequestTimes: number = retry + 1
    for (let requestTime = 0; requestTime < maxRequestTimes; requestTime++) {
      try {
        if (options.useWxCloud || this.useWxCloud) {
          response = await this.wxCloudCallFunction<T>(url, options)
        } else {
          response = await this.baseRequest<T>(url, options)
        }

        if (!!(response as any)?.code) {
          throw {
            error: (response as any).code,
            error_description: (response as any).message,
            error_uri: new URL(url).pathname,
          }
        }

        break
      } catch (responseError) {
        try {
          responseError.requestId = responseError.requestId || options.headers[RequestIdHeaderName] || ''
        } catch (error) {}
        if (options.withCredentials && responseError && responseError.error === ErrorType.UNAUTHENTICATED) {
          await this.setCredentials(null)
          return Promise.reject(responseError)
        }

        if (requestTime === retry || !responseError || responseError.error !== 'unreachable') {
          return Promise.reject(responseError)
        }
      }
      await this.sleep(OAuth2Client.retryInterval)
    }
    return response
  }

  public async wxCloudCallFunction<T>(url: string, options?: RequestOptions): Promise<T> {
    let result: T | null = null
    let responseError: ResponseError | null = null
    try {
      let userAgent = ''
      try {
        userAgent = await wx.getRendererUserAgent()
      } catch (error) {}
      const { result: responseResult } = await this.wxCloud.callFunction({
        name: 'httpOverCallFunction',
        data: {
          url,
          method: options.method,
          headers: {
            origin: 'https://servicewechat.com',
            'User-Agent': userAgent,
            ...options.headers,
          },
          body: options.body,
        },
      })

      if (responseResult?.body?.error_code) {
        responseError = responseResult?.body as ResponseError
        responseError.error_uri = getPathName(url)
      } else {
        result = responseResult?.body as T
      }
    } catch (error) {
      responseError = {
        error: ErrorType.UNREACHABLE,
        error_description: error.message,
        error_uri: getPathName(url),
      }
    }

    if (responseError) {
      throw responseError
    } else {
      return result
    }
  }

  /**
   * Get credentials.
   */
  public async getCredentials(): Promise<Credentials | null> {
    // If initialization is in progress, wait for it first
    await this.initializePromise

    return this._acquireLock(-1, async () => this._getCredentials())
  }

  /**
   * @deprecated 废弃接口，勿用. 如需获取凭证信息请使用 getCredentials 方法
   */
  public getCredentialsSync(): Credentials | null {
    const credentials: Credentials = this.localCredentials.getStorageCredentialsSync()
    return credentials
  }

  public getCredentialsAsync(): Promise<Credentials | null> {
    return this.getCredentials()
  }

  public async getScope(): Promise<string> {
    const credentials: Credentials = await this.localCredentials.getCredentials()
    if (!credentials) {
      const msg = 'credentials not found'
      this.onCredentialsError?.({ msg })
      return this.unAuthenticatedError(msg)
    }
    return credentials.scope
  }

  public async getGroups(): Promise<string[]> {
    const credentials: Credentials = await this.localCredentials.getCredentials()
    if (!credentials) {
      const msg = 'credentials not found'
      this.onCredentialsError?.({ msg })
      return this.unAuthenticatedError(msg)
    }
    return credentials.groups
  }

  /**
   * Refresh expired token.
   * @param {Credentials} credentials
   * @return {Promise<Credentials>}
   */
  public async refreshToken(credentials: Credentials, options?: { throwError?: boolean }): Promise<Credentials> {
    // If initialization is in progress, wait for it first
    await this.initializePromise

    return this._acquireLock(-1, async () => this._refreshToken(credentials, options))
  }

  /**
   * Internal refresh token method (called within lock)
   */
  private async _refreshToken(credentials: Credentials, options?: { throwError?: boolean }): Promise<Credentials> {
    return this.singlePromise.run('_refreshToken', async () => {
      // node sdk 场景下不需要刷新 token
      if (credentials?.scope === DEFAULT_NODE_ACCESS_SCOPE) {
        return credentials
      }

      if (!credentials || !credentials.refresh_token) {
        const msg = 'no refresh token found in credentials'
        this.onCredentialsError?.({ msg })
        return this.unAuthenticatedError(msg)
      }
      try {
        const newCredentials: Credentials = await this.refreshTokenFunc(credentials.refresh_token, credentials)
        await this.localCredentials.setCredentials(newCredentials)

        return newCredentials
      } catch (error) {
        if (options?.throwError) {
          throw error
        }
        if (error.error === ErrorType.INVALID_GRANT) {
          await this.localCredentials.setCredentials(null)
          const msg = error.error_description
          this.onCredentialsError?.({ msg })
          return this.unAuthenticatedError(msg)
        }
        this.onCredentialsError?.({ msg: error.error_description })
        return Promise.reject(error)
      }
    })
  }

  private async anonymousLogin(credentials: Credentials) {
    return this.singlePromise.run('_anonymousLogin', async () => {
      if (this.anonymousSignInFunc) {
        const c = await this.anonymousSignInFunc(credentials)
        credentials = c || (await this.localCredentials.getCredentials())
      } else {
        credentials = await this.anonymousSignIn(credentials)
      }

      return credentials
    })
  }

  /**
   * Check retry value.
   * @param {number} retry
   * @return {number}
   */
  private checkRetry(retry: number): number {
    let responseError: ResponseError | null = null
    if (typeof retry !== 'number' || retry < OAuth2Client.minRetry || retry > OAuth2Client.maxRetry) {
      responseError = {
        error: ErrorType.UNREACHABLE,
        error_description: 'wrong options param: retry',
      }
    }
    if (responseError) {
      throw responseError
    }
    return retry
  }

  /**
   * Format retry value.
   * @param {number} retry
   * @param {number} defaultVale
   * @return {number}
   */
  private formatRetry(retry: number, defaultVale: number): number {
    if (typeof retry === 'undefined') {
      return defaultVale
    }
    return this.checkRetry(retry)
  }

  /**
   * Sleep.
   * @param {number} ms
   * @return {Promise<void>}
   */
  private async sleep(ms: number): Promise<void> {
    return new Promise<void>((resolve) => {
      setTimeout(() => {
        resolve()
      }, ms)
    })
  }

  /**
   * anonymous signIn
   * @param {Credentials} credentials
   * @return {Promise<Credentials>}
   */
  private async anonymousSignIn(credentials: Credentials): Promise<Credentials> {
    return this.singlePromise.run('_anonymous', async () => {
      if (!credentials || credentials.scope !== 'anonymous') {
        return this.unAuthenticatedError('no anonymous in credentials')
      }
      try {
        const newCredentials: Credentials = await this.request(ApiUrls.AUTH_SIGN_IN_ANONYMOUSLY_URL, {
          method: 'POST',
          withBasicAuth: true,
          body: {},
        })
        await this.localCredentials.setCredentials(newCredentials)
        return newCredentials
      } catch (error) {
        if (error.error === ErrorType.INVALID_GRANT) {
          await this.localCredentials.setCredentials(null)
          return this.unAuthenticatedError(error.error_description)
        }
        return Promise.reject(error)
      }
    })
  }

  /**
   * Default refresh token function.
   * @param {string} refreshToken
   * @return {Promise<Credentials>}
   */
  private async defaultRefreshTokenFunc(refreshToken?: string, credentials?: Credentials): Promise<Credentials> {
    if (refreshToken === undefined || refreshToken === '') {
      const msg = 'refresh token not found'
      this.onCredentialsError?.({ msg })
      return this.unAuthenticatedError(msg)
    }

    let url: string = ApiUrls.AUTH_TOKEN_URL

    if (credentials?.version === 'v2') {
      url = ApiUrlsV2.AUTH_TOKEN_URL
    }

    const newCredentials: Credentials = await this.request(url, {
      method: 'POST',
      body: {
        client_id: this.clientId,
        client_secret: this.clientSecret,
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
      },
    })

    return { ...newCredentials, version: credentials?.version || 'v1' }
  }

  /**
   * Get deviceId
   */
  private async getDeviceId(): Promise<string> {
    if (this.deviceID) {
      return this.deviceID
    }
    let deviceId: string = await this.storage.getItem(DeviceIdSectionName)
    if (!(typeof deviceId === 'string' && deviceId.length >= 16 && deviceId.length <= 48)) {
      deviceId = uuidv4()
      await this.storage.setItem(DeviceIdSectionName, deviceId)
    }
    this.deviceID = deviceId
    return deviceId
  }
  /**
   * Generate unAuthenticated error.
   * @param {string} err
   * @return {Promise<T>}
   */
  private unAuthenticatedError<T>(err?: string): Promise<T> {
    const respErr: ResponseError = {
      error: ErrorType.UNAUTHENTICATED,
      error_description: err,
    }
    return Promise.reject(respErr)
  }

  /**
   * Debug logging helper
   */
  private _debug(...args: any[]): void {
    if (this.logDebugMessages) {
      console.log('[OAuth2Client]', ...args)
    }
  }

  /**
   * IMPORTANT:
   * 1. Never throw in this method, as it is called from the constructor
   * 2. Never return a session from this method as it would be cached over
   *    the whole lifetime of the client
   */
  private async _initialize(): Promise<{ error: Error | null }> {
    try {
      // If no getInitialSession callback is set, nothing to do
      if (!this.getInitialSession) {
        this._debug('#_initialize()', 'no getInitialSession callback set, skipping')
        return { error: null }
      }

      this._debug('#_initialize()', 'calling getInitialSession callback')

      try {
        const { data, error } = await this.getInitialSession()

        if (data?.session) {
          this._debug('#_initialize()', 'session obtained from getInitialSession', data.session)
          this._initSessionEmitted = true
          await this.localCredentials.setCredentials(data?.session, { isInit: true })
        }

        // Invoke callback for upper layer to handle user storage
        // This is called BEFORE lock is released so user storage is atomic with session
        if (this.onInitialSessionObtained) {
          this._debug('#_initialize()', 'calling onInitialSessionObtained callback')
          try {
            await this.onInitialSessionObtained(data, error)
          } catch (callbackError) {
            this._debug('#_initialize()', 'error in onInitialSessionObtained', callbackError)
            // Don't fail initialization if callback fails
          }
        }
        if (error) {
          this._debug('#_initialize()', 'error from getInitialSession', error)
          return { error }
        }

        return { error: null }
      } catch (err) {
        this._debug('#_initialize()', 'exception during getInitialSession', err)
        return { error: err instanceof Error ? err : new Error(String(err)) }
      }
    } catch (error) {
      this._debug('#_initialize()', 'unexpected error', error)
      return { error: error instanceof Error ? error : new Error(String(error)) }
    } finally {
      this._debug('#_initialize()', 'end')
    }
  }

  /**
   * Acquires a global lock based on the client ID.
   * This ensures that only one operation can modify credentials at a time.
   */
  private async _acquireLock<R>(acquireTimeout: number, fn: () => Promise<R>): Promise<R> {
    this._debug('#_acquireLock', 'begin', acquireTimeout)

    try {
      if (this.lockAcquired) {
        // Lock is already acquired, queue the operation
        const last = this.pendingInLock.length ? this.pendingInLock[this.pendingInLock.length - 1] : Promise.resolve()

        const result = (async () => {
          await last
          return await fn()
        })()

        this.pendingInLock.push((async () => {
          try {
            await result
          } catch (_e: any) {
            // we just care if it finished
          }
        })(),)

        return result
      }

      // Acquire the lock
      this._debug('#_acquireLock', 'acquiring lock for client', this.clientId)

      try {
        this.lockAcquired = true

        const result = fn()

        this.pendingInLock.push((async () => {
          try {
            await result
          } catch (_e: any) {
            // we just care if it finished
          }
        })(),)

        await result

        // keep draining the queue until there's nothing to wait on
        while (this.pendingInLock.length) {
          const waitOn = [...this.pendingInLock]
          await Promise.all(waitOn)
          this.pendingInLock.splice(0, waitOn.length)
        }

        return await result
      } finally {
        this._debug('#_acquireLock', 'releasing lock for client', this.clientId)
        this.lockAcquired = false
      }
    } finally {
      this._debug('#_acquireLock', 'end')
    }
  }

  /**
   * Internal method to get credentials (called within lock)
   */
  private async _getCredentials(): Promise<Credentials | null> {
    let credentials: Credentials = await this.localCredentials.getCredentials()
    // node adapter 场景下，scope 为 DEFAULT_NODE_ACCESS_SCOPE，不需要刷新 token
    if (credentials?.scope === DEFAULT_NODE_ACCESS_SCOPE) {
      return credentials
    }
    if (!credentials) {
      const msg = 'credentials not found'
      this.onCredentialsError?.({ msg })
      return this.unAuthenticatedError(msg)
    }
    if (isCredentialsExpired(credentials)) {
      if (credentials.refresh_token) {
        try {
          credentials = await this._refreshToken(credentials)
        } catch (error) {
          if (credentials.scope === 'anonymous') {
            credentials = await this.anonymousLogin(credentials)
          } else {
            this.onCredentialsError?.({ msg: error.error_description })
            return Promise.reject(error)
          }
        }
      } else if (credentials.scope === 'anonymous') {
        credentials = await this.anonymousLogin(credentials)
      } else {
        const msg = 'no refresh token found in credentials'
        this.onCredentialsError?.({ msg })
        return this.unAuthenticatedError(msg)
      }
    }
    return credentials
  }

  /**
   * 判断 credentials 是否代表有效的用户登录态（非 accessKey / node-sdk-scope）。
   */
  private _isUserCredentials(credentials: Credentials | null): boolean {
    return !!credentials?.access_token
      && credentials.scope !== 'accessKey'
      && credentials.scope !== DEFAULT_NODE_ACCESS_SCOPE
  }

  /**
   * 根据 credentials 与上下文统一触发 eventBus 事件。
   * 收敛 _handleCredentialsChange / _handleBroadcastMessage / _onVisibilityChanged 三处的重复逻辑。
   */
  private _fireCredentialsEvents(
    credentials: Credentials | null,
    isInit: boolean,
    hadCredentials: boolean,
  ): void {
    const isUserCredentials = this._isUserCredentials(credentials)

    if (!isUserCredentials) {
      // 非用户登录态：仅当之前确实持有用户登录态时才触发 SIGNED_OUT
      if (hadCredentials) {
        this.eventBus?.fire(EVENTS.AUTH_STATE_CHANGED, { event: AUTH_STATE_CHANGED_TYPE.SIGNED_OUT })
        this.eventBus?.fire(EVENTS.LOGIN_STATE_CHANGED, { eventType: LOGIN_STATE_CHANGED_TYPE.SIGN_OUT })
      }
    } else if (isInit) {
      // 初始化阶段恢复 session
      this.eventBus?.fire(EVENTS.AUTH_STATE_CHANGED, { event: AUTH_STATE_CHANGED_TYPE.INITIAL_SESSION })
    } else if (hadCredentials) {
      // Token 刷新
      this.eventBus?.fire(EVENTS.AUTH_STATE_CHANGED, { event: AUTH_STATE_CHANGED_TYPE.TOKEN_REFRESHED })
    } else {
      // 首次登录
      this.eventBus?.fire(EVENTS.AUTH_STATE_CHANGED, { event: AUTH_STATE_CHANGED_TYPE.SIGNED_IN })
      this.eventBus?.fire(EVENTS.LOGIN_STATE_CHANGED, { eventType: LOGIN_STATE_CHANGED_TYPE.SIGN_IN })
    }
  }

  /**
   * Credentials 变更的统一回调入口。
   * 由 LocalCredentials.setCredentials 在所有写入完成后调用，
   * 负责：① eventBus 事件广播 ② BroadcastChannel 跨标签页通知。
   */
  private _handleCredentialsChange(credentials: Credentials | null, context: CredentialsChangeContext): void {
    this._fireCredentialsEvents(credentials, context.isInit, context.hadCredentials)

    // 跨标签页广播（仅 web 环境生效）
    this._broadcastCredentials(credentials, context)
  }

  /**
   * 初始化 BroadcastChannel（仅浏览器环境）。
   * 频道名与 storage key 一致，确保同类 client 共享同一频道。
   * 小程序/SSR/Node.js 环境自动跳过。
   */
  private _initBroadcastChannel(): void {
    if (this.broadcastChannel !== undefined) {
      return // 已初始化
    }

    try {
      // eslint-disable-next-line no-restricted-globals
      if (typeof BroadcastChannel !== 'undefined') {
        this.broadcastChannelName = `credentials_${this.clientId}`
        this.broadcastChannel = new BroadcastChannel(this.broadcastChannelName)
        this.broadcastChannel.addEventListener('message', (event: any) => {
          this._handleBroadcastMessage(event.data)
        })
        this._debug('#_initBroadcastChannel', `channel created: ${this.broadcastChannelName}`)
      }
    } catch (error) {
      // 小程序/SSR/Node.js 无 BroadcastChannel，安静跳过
      this.broadcastChannel = null
      this._debug('#_initBroadcastChannel', 'BroadcastChannel not available, skip', error)
    }
  }

  /**
   * 向其他标签页广播 credentials 变更。
   */
  private _broadcastCredentials(credentials: Credentials | null, context: CredentialsChangeContext): void {
    if (!this.broadcastChannel) {
      return
    }
    try {
      this.broadcastChannel.postMessage({
        type: 'credentials_changed',
        credentials,
        context,
      })
    } catch (error) {
      this._debug('#_broadcastCredentials', 'postMessage error', error)
    }
  }

  /**
   * 接收其他标签页广播的 credentials 变更。
   * 仅同步内存缓存 + 触发本地事件（不重复写 storage，发送端已写入）。
   */
  private _handleBroadcastMessage(data: any): void {
    try {
      if (!data || data.type !== 'credentials_changed') {
        return
      }

      const { context } = data as {
        type: string
        credentials: Credentials | null
        context: CredentialsChangeContext
      }
      let { credentials } = data as {
        type: string
        credentials: Credentials | null
        context: CredentialsChangeContext
      }

      // 恢复 Date 对象（postMessage 将其序列化为 ISO string）
      if (credentials?.expires_at && typeof credentials.expires_at === 'string') {
        credentials = {
          ...credentials,
          expires_at: new Date(credentials.expires_at),
        }
      }

      // 凭据未变化则跳过（例如收到自己发出的广播）
      // 与 _onVisibilityChanged 的 oldToken/newToken 对比策略一致
      if (credentials?.access_token === this.localCredentials.getMemoryCredentials()?.access_token) {
        return
      }

      // 记录接收方 sync 之前是否已有凭证（非发送方的 hadCredentials）
      // 发送方的 hadCredentials 反映发送方的状态，与接收方无关
      const receiverHadCredentials = !!(this.localCredentials.getMemoryCredentials()?.access_token)

      // 同步内存缓存（不写 storage，发送端已写入）
      this.localCredentials.syncMemoryCredentials(credentials)

      // 触发本地事件
      this._fireCredentialsEvents(credentials, context.isInit, receiverHadCredentials)
    } catch (error) {
      this._debug('#_handleBroadcastMessage', 'error', error)
    }
  }

  /**
   * 注册页面可见性变化监听。
   * 标签页从隐藏恢复可见时，从 storage 重新加载凭证，
   * 以便感知在隐藏期间其他标签页的登录/登出/刷新。
   * 仅在浏览器环境生效，SSR/小程序自动跳过。
   */
  private _initVisibilityListener(): void {
    try {
      if (typeof window === 'undefined' || typeof document === 'undefined' || typeof window.addEventListener !== 'function') {
        return
      }
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      this.visibilityEventHandler = async () => {
        try {
          await this._onVisibilityChanged()
        } catch (error) {
          this._debug('#visibilityChangedCallback', 'error', error)
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      window.addEventListener('visibilitychange', this.visibilityEventHandler)
    } catch (error) {
      this._debug('#_initVisibilityListener', error)
    }
  }

  /**
   * Callback registered with `window.addEventListener('visibilitychange')`.
   * 标签页隐藏→恢复时，从 storage 重新加载凭证。
   * 与 supabase GoTrueClient._onVisibilityChanged 对齐。
   */
  private async _onVisibilityChanged(): Promise<void> {
    if (document.visibilityState !== 'visible') {
      return
    }

    this._acquireLock(-1, async () => {
      // 锁内二次确认（锁等待期间状态可能已变化）
      if (document.visibilityState !== 'visible') {
        return
      }
      try {
        const oldCredentials = this.localCredentials.getMemoryCredentials()
        const newCredentials = await this.localCredentials.getCredentials()

        const oldToken = oldCredentials?.access_token
        const newToken = newCredentials?.access_token

        // 仅当凭证发生变化时才触发事件（避免无意义广播）
        if (oldToken === newToken) {
          return
        }

        const hadCredentials = !!oldToken
        this._fireCredentialsEvents(newCredentials, false, hadCredentials)
      } catch (error) {
        this._debug('#_onVisibilityChanged', 'reload credentials error', error)
      }
    })
  }
}
